import { type SubscriptionTier } from 'types/team';

/**
 * Returned by the OTP verify endpoint when a code has absorbed its allowance of
 * wrong guesses and has been discarded. Matches the backend constant.
 */
export const OTP_ATTEMPTS_EXCEEDED_CODE = 'otp_attempts_exceeded';

export type ProjectLevelPermissions =
  | 'read_project'
  | 'edit_project'
  | 'chat_project'
  | 'edit_sources';
export type TeamLevelPermissions = 'read_team' | 'edit_team';

export interface UserProject {
  id: string;
  project_name: string;
  is_internal: boolean;
  permissions: {
    all: Array<ProjectLevelPermissions>;
    user: Array<ProjectLevelPermissions>;
    group: Array<ProjectLevelPermissions>;
  };
  internal_assistant_integration?: InternalAssistantIntegrationRef | null;
}

interface InternalAssistantIntegrationRef {
  id: string;
  is_enabled: boolean;
  is_setup_complete: boolean;
}

export interface UserTeam {
  id: string;
  name: string;
  permissions: {
    all: Array<TeamLevelPermissions>;
    user: Array<TeamLevelPermissions>;
    group: Array<TeamLevelPermissions>;
  };
  projects: Array<UserProject>;
  tier: SubscriptionTier;
  // The trial window, null on paid tiers; the start is the creation instant.
  trial_started_at: string | null;
  trial_ends_at: string | null;
  subscription_settings: {
    cta_enabled: boolean;
    show_warning_banner: boolean;
    warning_banner_text?: string;
  };
}

export type Role = 'owner' | 'member' | 'user';

/**
 * What `/org/v1/me/` returns. A self-serve account has no team until onboarding
 * creates one, so only the boundary in `lib/auth.tsx` sees this shape.
 */
export interface SessionUser {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  team: UserTeam | null;
  role: Role;
  is_staff_viewing_external_team: boolean;
}

/** A session that has a team, which is every screen behind the app shell. */
export interface User extends Omit<SessionUser, 'team'> {
  team: UserTeam;
}
